import axios from 'utils/axios';
import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';

import {
    Box,
    IconButton,
    Divider,
    Skeleton,
    Typography,
    Card,
    CardContent,
    Table,
    Button,
    TableCell,
    TableBody,
    TableRow,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    CircularProgress
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotesIcon from '@mui/icons-material/Notes';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';

import AddEditSchool from './AddEditSchool';

const ProviderDetailsSidebar = ({ onClose, providerIntakeId, getStatusIcon, schoolYearId }) => {
    const [loading, setLoading] = useState(true);
    const [providerDetails, setProviderDetails] = useState({});
    const [schoolCurrentlyEditingId, setSchoolCurrentlyEditingId] = useState(null);
    const [taskCurrentlyOpenId, setTaskCurrentlyOpenId] = useState(null);
    const [cancelReactivateProviderDialogOpen, setCancelReactivateProviderDialogOpen] = useState(false);
    const [cancelReactivateLoading, setCancelReactivateLoading] = useState(false);
    const cardRef = useRef(null);
    const [addEditSchoolOptions, setAddEditSchoolOptions] = useState({
        schools: [],
        jobTitles: [],
        taxTypes: []
    });

    const theme = useTheme();

    const isAdmin = localStorage.roles.includes('Admin');

    const taskTypeOrder = ['Viventium', 'PETS', 'TAARIS'];

    const getAndSetARDetails = async () => {
        const { data } = await axios.get(`/api/providerintake/ardetails/${providerIntakeId}`);
        setProviderDetails({ ...providerDetails, accountResources: data });
    };

    const getProviderDetails = async () => {
        try {
            const { data } = await axios.get(`/api/providerintake/details/${providerIntakeId}`);
            setProviderDetails(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching provider details:', error);
        }
    };

    useEffect(() => {
        const getAddEditSchoolOptions = async () => {
            try {
                const { data } = await axios.get(`/api/providerintake/schooloptions?schoolYearId=${schoolYearId}`);
                setAddEditSchoolOptions(data);
            } catch (error) {
                console.error('Error fetching school options:', error);
            }
        };

        getProviderDetails();
        getAddEditSchoolOptions();
    }, []);

    const toggleAddEditSchool = (id) => {
        setSchoolCurrentlyEditingId(schoolCurrentlyEditingId === id ? null : id);
    };

    const toggleTaskNotes = (id) => {
        setTaskCurrentlyOpenId(taskCurrentlyOpenId === id ? null : id);
    };

    const tableCellStyles = { paddingRight: 0.75, paddingLeft: 0.75 };
    const unsetBottomBorderStyle = { '& .MuiTableCell-root': { borderBottom: 'unset' } };
    const unsetBottomBorderIfLast = { '&:last-child td, &:last-child th': { borderBottom: 'unset' } };

    const isOpenStatus = providerDetails.status === 'Open';
    const isEditable = (status) => status === 'Draft' && isOpenStatus;

    const getTableRowIcon = (id, iconWithProps) =>
        schoolCurrentlyEditingId === id ? <CloseIcon {...iconWithProps.props} /> : iconWithProps;

    const providerIntakeDetails = {
        providerIntakeId,
        schoolYearId,
        providerId: providerDetails.providerId,
        providerName: providerDetails.providerName
    };

    const handleCancelReactivateProviderDialogClose = () => {
        setCancelReactivateProviderDialogOpen(false);
    };

    const cancelReactivateProvider = async () => {
        setCancelReactivateLoading(true);
        handleCancelReactivateProviderDialogClose();

        try {
            await axios.post(`/api/providerintake/cancelreactivate/${providerIntakeId}?cancel=${isOpenStatus}`);

            if (isOpenStatus) {
                onClose();
            } else {
                await getProviderDetails();
                setCancelReactivateLoading(false);
            }
        } catch (ex) {
            console.error('Error canceling/reactivating provider:', ex);
            setCancelReactivateLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '95vh', paddingBottom: 5.5 }}>
            <div style={{ marginRight: '15px', marginLeft: '15px', marginTop: '10px' }}>
                <div>
                    <h4>
                        Provider Details
                        <IconButton
                            style={{
                                float: 'right',
                                padding: '0px 0px 0px 0px',
                                marginTop: '-5px'
                            }}
                            onClick={onClose}
                        >
                            <CancelIcon />
                        </IconButton>
                    </h4>
                </div>
                <Divider />
                <div style={{ marginTop: '12px', marginBottom: '12px' }} ref={cardRef}>
                    {loading ? (
                        <Stack spacing={2}>
                            <Skeleton variant="rounded" height={isAdmin ? 90 : 60} />
                            <Skeleton variant="rounded" height={100} />
                        </Stack>
                    ) : (
                        <Card
                            sx={{
                                '& .MuiCardContent-root:last-child': {
                                    paddingBottom: 1
                                }
                            }}
                        >
                            <CardContent sx={{ p: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="h4" color={theme.palette.secondary.main} fontWeight="bold">
                                            {providerDetails.providerName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            <span>{providerDetails.isRepeat ? 'Repeat' : 'New Hire'}</span>
                                            <span>{providerDetails.schoolYear ? ` - ${providerDetails.schoolYear}` : ''}</span>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {getStatusIcon(providerDetails.status, 0.5)}
                                        {providerDetails.status}
                                    </Box>
                                </Stack>
                            </CardContent>
                            {isAdmin && (
                                <CardContent sx={{ p: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="body2" color="text.secondary">
                                            Supervisor
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {providerDetails.supervisorName}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            )}
                            <CardContent
                                sx={{
                                    p: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderColor: theme.palette.primary.light,
                                    borderRadius: 2,
                                    marginTop: 2
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={theme.palette.primary.main}
                                    sx={{ marginTop: 1, marginLeft: 0.7, marginBottom: 1 }}
                                >
                                    Tasks
                                </Typography>
                                <Stack spacing={2.5} padding={0.75} paddingBottom={1.25} paddingTop={1}>
                                    {taskTypeOrder.map((tt) => {
                                        const task = providerDetails.tasks.find((t) => t.type === tt);
                                        if (!task) return null;

                                        return (
                                            <React.Fragment key={task.id}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="left">
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            {dayjs(task.createdDate).format('MM/DD/YY')}
                                                        </Typography>
                                                        <FiberManualRecordIcon sx={{ color: task.statusColor }} />
                                                        <Stack>
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {task.type}
                                                            </Typography>
                                                            <Typography variant="subtitle2" color={task.statusColor}>
                                                                {task.status}
                                                            </Typography>
                                                            {task.completedDate ? (
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    Completed: {dayjs(task.completedDate).format('MM/DD/YY')}
                                                                </Typography>
                                                            ) : (
                                                                task.dueDate && (
                                                                    <Typography variant="subtitle2" color="text.secondary">
                                                                        Due: {dayjs(task.dueDate).format('MM/DD/YY')}
                                                                    </Typography>
                                                                )
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                    {task.notes && (
                                                        <IconButton onClick={() => toggleTaskNotes(task.id)}>
                                                            <NotesIcon />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                                {taskCurrentlyOpenId === task.id && (
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            backgroundColor: theme.palette.grey[50],
                                                            borderRadius: 1.5
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            fontWeight="bold"
                                                            sx={{ mb: 0.5 }}
                                                        >
                                                            Notes
                                                        </Typography>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            {task.notes}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Stack>
                            </CardContent>
                            <CardContent
                                sx={{
                                    p: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderColor: theme.palette.primary.light,
                                    borderRadius: 2,
                                    marginTop: 3
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={theme.palette.primary.main}
                                    sx={{ marginTop: 1, marginLeft: 0.7, marginBottom: 1 }}
                                >
                                    Schools
                                </Typography>
                                <Table>
                                    <TableBody>
                                        {providerDetails.accountResources.map((ar, i) => (
                                            <React.Fragment key={ar.id}>
                                                <TableRow
                                                    sx={
                                                        schoolCurrentlyEditingId === ar.id
                                                            ? unsetBottomBorderStyle
                                                            : unsetBottomBorderIfLast
                                                    }
                                                >
                                                    <TableCell sx={tableCellStyles}>
                                                        <Typography fontSize="13.5px">{ar.school?.name}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...tableCellStyles, minWidth: 110 }}>
                                                        <Typography fontSize="13.5px">{ar.jobTitle?.name}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={tableCellStyles}>
                                                        <IconButton onClick={() => toggleAddEditSchool(ar.id)}>
                                                            {getTableRowIcon(
                                                                ar.id,
                                                                isEditable(ar.status) ? (
                                                                    <EditIcon fontSize="small" color="secondary" />
                                                                ) : (
                                                                    <VisibilityIcon fontSize="small" color="secondary" />
                                                                )
                                                            )}
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                {schoolCurrentlyEditingId === ar.id && (
                                                    <TableRow sx={unsetBottomBorderIfLast}>
                                                        <TableCell colSpan={3} sx={{ paddingTop: 0.5 }}>
                                                            <AddEditSchool
                                                                accountResourceDetails={ar}
                                                                options={addEditSchoolOptions}
                                                                readOnly={!isEditable(ar.status)}
                                                                providerIntakeDetails={providerIntakeDetails}
                                                                getAndSetARDetails={getAndSetARDetails}
                                                                setSchoolCurrentlyEditingId={setSchoolCurrentlyEditingId}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {isOpenStatus && (
                                            <>
                                                <TableRow sx={unsetBottomBorderStyle}>
                                                    <TableCell
                                                        colSpan={3}
                                                        align="right"
                                                        sx={{ ...tableCellStyles, paddingTop: 1, paddingBottom: 1 }}
                                                    >
                                                        <IconButton sx={{ padding: 1 }} onClick={() => toggleAddEditSchool('new')}>
                                                            {getTableRowIcon('new', <AddIcon color="primary" sx={{ fontSize: '28px' }} />)}
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                {schoolCurrentlyEditingId === 'new' && (
                                                    <TableRow sx={unsetBottomBorderStyle}>
                                                        <TableCell colSpan={3} sx={{ paddingTop: 0.5 }}>
                                                            <AddEditSchool
                                                                options={addEditSchoolOptions}
                                                                providerIntakeId={providerIntakeId}
                                                                schoolYearId={schoolYearId}
                                                                getAndSetARDetails={getAndSetARDetails}
                                                                setSchoolCurrentlyEditingId={setSchoolCurrentlyEditingId}
                                                                providerIntakeDetails={providerIntakeDetails}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
            {(isOpenStatus || providerDetails.status === 'Canceled') && (
                <div
                    style={{
                        paddingBottom: '18px',
                        paddingTop: '18px',
                        marginRight: '15px',
                        marginLeft: '15px',
                        position: 'fixed',
                        bottom: '0px',
                        backgroundColor: 'white',
                        zIndex: 1
                    }}
                >
                    <div
                        style={{
                            width: cardRef.current?.getBoundingClientRect().width,
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setCancelReactivateProviderDialogOpen(true)}
                            disabled={cancelReactivateLoading}
                            sx={{
                                backgroundColor: isOpenStatus ? theme.palette.grey[500] : theme.palette.primary.main,
                                borderRadius: 1,
                                '&:hover': {
                                    backgroundColor: isOpenStatus ? theme.palette.grey[500] : theme.palette.primary.main
                                }
                            }}
                        >
                            {cancelReactivateLoading && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px'
                                    }}
                                />
                            )}
                            {isOpenStatus ? 'Cancel Provider' : 'Reactivate Provider'}
                        </Button>
                    </div>
                </div>
            )}
            <Dialog open={cancelReactivateProviderDialogOpen} onClose={handleCancelReactivateProviderDialogClose}>
                <DialogTitle>{isOpenStatus ? 'Cancel Provider' : 'Reactivate Provider'}</DialogTitle>
                <DialogContent>Are you sure you want to {isOpenStatus ? 'cancel' : 'reactivate'} this provider?</DialogContent>
                <DialogActions>
                    <Button onClick={cancelReactivateProvider}>Yes</Button>
                    <Button onClick={handleCancelReactivateProviderDialogClose}>No</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProviderDetailsSidebar;
